/** @jsxImportSource @emotion/react */
import React from 'react'
import { jsx } from '@emotion/react/macro'
import { BRAND_COLOR, HealthLabelsKeys } from 'lib/constants'
import _ from 'lodash'
import { SortOption, SortOrder, TableColumnConfig, TranslationKey, TranslationGroup, trans } from 'lib/types'
import { createSortOption } from 'lib/utils/common'
import {
  HighlightOff as HighlightOffIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
} from '@material-ui/icons'
import { ChannelsColumnKeys } from 'lib/constants/keys/channels'
import { Channel } from 'lib/types/channels'
import { Button, CircularProgress } from '@material-ui/core'
import { getHealth } from 'lib/utils'
import PTTButton from 'components/partials/PTTButton'
import { useVoice } from 'contexts/VoiceProvider'

/* -------------------------------------------------------------------------- */
/* Normalizers (keep header/table/voice consistent)                           */
/* -------------------------------------------------------------------------- */
export function normalizeChannelId(c: any): string {
  return String(
    c?.id ??
      c?.channelId ??
      c?.channel_id ??
      c?.name ?? // fallback to name
      '',
  )
}
export function normalizeChannelName(c: any): string {
  return c?.name ?? c?.channelName ?? c?.displayName ?? c?.title ?? ''
}

// Simple collator kept (not used here directly but fine)
const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' })

export function getFilterOptions() {
  const translation: TranslationGroup = trans.group(TranslationKey.BEACONS_TABLE_VIEW)
  void translation
  return ['All channels']
}

export function getFilterOptionKeys() {
  return [HealthLabelsKeys.UNKNOWN, HealthLabelsKeys.CRITICAL, HealthLabelsKeys.MODERATE, HealthLabelsKeys.HEALTHY]
}

export function getGatewayTooltip(_channel: Channel) {
  const translation: TranslationGroup = trans.merge(TranslationKey.HEALTH_TOOLTIPS)
  void translation
  return `Channel `
}

// union of the values of HealthLabelsKeys
type HealthLabel = (typeof HealthLabelsKeys)[keyof typeof HealthLabelsKeys]

function getChannelHealth(channel: Channel): HealthLabel {
  const label = getHealth ? getHealth(channel, true) : undefined
  if (!label || label === HealthLabelsKeys.UNKNOWN) return HealthLabelsKeys.HEALTHY
  const validValues = Object.values(HealthLabelsKeys) as HealthLabel[]
  if (!validValues.includes(label as any)) return HealthLabelsKeys.HEALTHY
  return label as HealthLabel
}

/* -------------------------------------------------------------------------- */
/* Membership cell                                                            */
/* -------------------------------------------------------------------------- */
const NOOP = () => {}

type MembershipProps = {
  channel: Channel
  // legacy inputs:
  joinedChannelId: string | null
  joiningChannelId: string | null
  // optional extras:
  joinedChannelName?: string | null
  voiceReady?: boolean
  zelloError?: string
  onJoin?: (c: Channel) => void
  onLeave?: () => void
  onPTTStart?: () => void
  onPTTStop?: () => void
  // NEW: drive the row PTT UI from page-level session state
  zelloStatus?: 'idle' | 'connecting' | 'connected' | 'lost' | 'failed' | 'disconnected' | 'kicked'
  channelBusy?: boolean // intentionally unused for Busy label; Busy comes from zelloError
  txState?: 'idle' | 'sending'
}

function MembershipButton({
  channel,
  joinedChannelId,
  joinedChannelName,
  joiningChannelId,
  voiceReady,
  onJoin,
  onLeave,
  onPTTStart,
  onPTTStop,
  zelloStatus = 'idle',
  // channelBusy = false, // not used for label anymore
  txState = 'idle',
  zelloError,
}: MembershipProps) {
  const cid = normalizeChannelId(channel)
  const cname = normalizeChannelName(channel)

  const { isChannelReady, getSessionState, reconnect } = useVoice()

  // Each row reads its own session state (one Zello WebSocket per channel),
  // so per-row PTT reflects exactly that channel's connection — not the
  // global aggregate.
  const sessionState = getSessionState(cname)
  const sessionStatus = sessionState?.status ?? zelloStatus
  const sessionTxState = sessionState?.tx ?? txState
  const sessionError = sessionState?.error ?? zelloError

  // Consider joined if either id OR name matches the current session
  const isJoined = joinedChannelId === cid || (!!joinedChannelName && joinedChannelName === cname)
  const isJoining = joiningChannelId === cid
  const someoneJoining = !!joiningChannelId

  // Only show initializing spinner when voiceReady is explicitly false
  const showInitializing = voiceReady === false && !isJoined && !isJoining

  // Treat "logged on but channel hasn't emitted on_channel_status: online yet"
  // as connecting — sending start_stream during this window comes back as
  // "channel is not ready" (Zello spec §158-160).
  const channelReady = isChannelReady(cname)
  const connectionFailed = Boolean(
    sessionState?.connectionFailed && isJoined && !channelReady,
  )
  const connecting =
    !connectionFailed &&
    (sessionStatus === 'connecting' ||
      (isJoined && sessionStatus === 'connected' && !channelReady))
  const sending = sessionTxState === 'sending'

  // PTT disabled logic (row scope; keep stricter than header)
  const pttDisabled = !isJoined || isJoining || voiceReady === false || sessionStatus !== 'connected' || !channelReady

  const cellStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'max-content max-content',
    alignItems: 'center',
    justifyItems: 'start',
    gap: 8,
  }

  return (
    <div style={cellStyle}>
      {isJoined ? (
        <>
          <Button
            onClick={() => onLeave?.()}
            size="small"
            variant="outlined"
            style={{ color: BRAND_COLOR, borderColor: BRAND_COLOR, width: 'auto', minWidth: 80 }}
          >
            Leave
          </Button>

          <PTTButton
            disabled={pttDisabled}
            connecting={connecting}
            sending={sending}
            connectionFailed={connectionFailed}
            onReconnect={reconnect}
            lastError={sessionError} // ← Busy derived ONLY from this error text
            onStart={onPTTStart || NOOP}
            onStop={onPTTStop || NOOP}
          />
        </>
      ) : (
        <>
          <Button
            size="small"
            variant="contained"
            disabled={someoneJoining || showInitializing}
            onClick={() => onJoin && onJoin(channel)}
            style={{
              backgroundColor: !someoneJoining && !showInitializing ? BRAND_COLOR : undefined,
              color: !someoneJoining && !showInitializing ? '#fff' : undefined,
              width: 'auto',
              minWidth: 80,
            }}
          >
            {isJoining || showInitializing ? <CircularProgress size={16} thickness={5} /> : 'Join'}
          </Button>
          {/* Invisible placeholder so layout doesn't shift */}
          <span style={{ visibility: 'hidden' }}>
            <PTTButton disabled connecting={false} sending={false} onStart={NOOP} onStop={NOOP} />
          </span>
        </>
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Columns                                                                    */
/* -------------------------------------------------------------------------- */

export const getSortOptions: () => SortOption<ChannelsColumnKeys>[] = () => {
  const translation: TranslationGroup = trans.merge(TranslationKey.BEACONS_TABLE_VIEW)
  return _.flatten(
    _.map(
      _.filter(ChannelsColumnKeys, c => c !== ChannelsColumnKeys.LastSeenAt),
      c => [
        createSortOption(c, SortOrder.Descending, `${translation[c]} ↓`),
        createSortOption(c, SortOrder.Ascending, `${translation[c]} ↑`),
      ],
    ),
  )
}

export const getColumnConfigs: (membership?: {
  joinedChannelId: string | null
  joinedChannelName?: string | null
  joiningChannelId: string | null
  voiceReady?: boolean
  onJoin?: (c: Channel) => void
  onLeave?: () => void
  onPTTStart?: () => void
  onPTTStop?: () => void
  // NEW: session-driven UI (from ChannelsPage)
  zelloStatus?: 'idle' | 'connecting' | 'connected' | 'lost' | 'failed' | 'disconnected' | 'kicked'
  zelloError?: string
  channelBusy?: boolean
  txState?: 'idle' | 'sending'
}) => TableColumnConfig<any, Channel>[] = membership => {
  const healthLabelsTranslation: TranslationGroup = trans.merge(TranslationKey.HEALTH_LABELS)

  return [
    {
      header: ChannelsColumnKeys.Health,
      label: 'Health',
      renderFn: (channel: Channel) => {
        const health = getChannelHealth(channel)
        let icon = null
        switch (health) {
          case HealthLabelsKeys.HEALTHY:
            icon = <CheckCircleOutlineIcon style={{ color: '#5FD078' }} />
            break
          case HealthLabelsKeys.MODERATE:
            icon = <RemoveCircleOutlineIcon style={{ color: '#E89F0B' }} />
            break
          case HealthLabelsKeys.OFFLINE:
          case HealthLabelsKeys.CRITICAL:
            icon = <HighlightOffIcon style={{ color: '#F44336' }} />
            break
          default:
            icon = '?'
            break
        }
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ paddingRight: '10px', alignItems: 'center', display: 'flex' }}>{icon}</div>
            <div style={{ lineHeight: '10px' }}>{healthLabelsTranslation[health]}</div>
          </div>
        )
      },
    },
    {
      header: ChannelsColumnKeys.Name,
      label: 'Name',
      renderFn: (channel: Channel) => normalizeChannelName(channel),
    },
    {
      header: 'membership',
      label: 'Channel',
      renderFn: (channel: Channel) => (
        <MembershipButton
          channel={channel}
          joinedChannelId={membership?.joinedChannelId ?? null}
          joinedChannelName={membership?.joinedChannelName ?? null}
          joiningChannelId={membership?.joiningChannelId ?? null}
          voiceReady={membership?.voiceReady}
          onJoin={membership?.onJoin}
          onLeave={membership?.onLeave}
          onPTTStart={membership?.onPTTStart}
          onPTTStop={membership?.onPTTStop}
          zelloStatus={membership?.zelloStatus}
          channelBusy={Boolean(membership?.channelBusy)} // kept for future, not used for label
          txState={membership?.txState}
          zelloError={membership?.zelloError}
        />
      ),
    },
  ]
}
